import { create } from "zustand"

import "../graphql/payFileList.query"

import {
  GetPayFileListDocument,
  type GetPayFileListQuery,
  type PayFileListFieldsFragment,
  type PayFileListInput,
  type PayFileSortField,
  type PayFileStatus,
  type PayFileType,
  type SortingOrder,
} from "@/graphql/codegen/graphql"
import { type SearchFilters, createSearchStore } from "@/stores/createSearchStore"

export type PayFileSearchFilters = SearchFilters & {
  field?: PayFileSortField
  order?: SortingOrder
  statuses?: PayFileStatus[]
  type?: PayFileType
  scheduledPayDateFrom?: string
  scheduledPayDateTo?: string
}

export const deduplicatePayFiles = (
  newRows: PayFileListFieldsFragment[],
  existing: PayFileListFieldsFragment[]
): PayFileListFieldsFragment[] => {
  const seen = new Set(existing.map((payFile) => payFile.payFileId))
  return [...existing, ...newRows.filter((payFile) => !seen.has(payFile.payFileId))]
}

export const getPayFileSearchInput = (
  filters: PayFileSearchFilters,
  offset: number,
  limit: number
): PayFileListInput => {
  const { field, order, statuses, type, scheduledPayDateFrom, scheduledPayDateTo } = filters
  // An empty selection and an absent one are the same "no status filter" query, so they are collapsed to keep one
  // urql cache key for it.
  const normalizedStatuses = statuses?.length ? statuses : undefined

  return {
    offset,
    limit,
    sort: field && order ? { field, order } : undefined,
    filter: { statuses: normalizedStatuses, type, scheduledPayDateFrom, scheduledPayDateTo },
  }
}

interface ExtractedPayFileListResponse {
  __typename: GetPayFileListQuery["payFile"]["list"]["__typename"]
  data: PayFileListFieldsFragment[]
  error?: { message: string }
}

export const extractPayFileListResponse = (data: GetPayFileListQuery): ExtractedPayFileListResponse => {
  const result = data.payFile.list
  if (result.__typename === "PayFileListSuccess") {
    return {
      __typename: result.__typename,
      // Codegen masks fragment spreads behind `$fragmentRefs`; the runtime rows are exactly the
      // fragment's fields, so unmasking here keeps every consumer typed to what the query selects.
      // Deliberately not via `unknown` — the overlap check is what catches the spread being dropped.
      data: result.payFiles as PayFileListFieldsFragment[],
    }
  }
  if (result.__typename === "PayFileListFailure") {
    return {
      __typename: result.__typename,
      data: [],
      error: result.error,
    }
  }
  // createSearchStore only rethrows when __typename ends in "Failure", so an unknown shape has to
  // throw here or the store would swallow it and render an empty table over a backend regression.
  throw new Error(`Unexpected response type: ${(result as { __typename: string }).__typename}`)
}

export const usePayFileSearchStore = create(
  createSearchStore<PayFileListFieldsFragment, PayFileSearchFilters, GetPayFileListQuery>({
    documentNode: GetPayFileListDocument,
    deduplicateData: deduplicatePayFiles,

    getQueryInput: getPayFileSearchInput,

    getQueryVariables: (queryInput) => ({ input: queryInput }),

    extractDataFromResponse: extractPayFileListResponse,
  })
)

// Writes a status change found by polling into the loaded rows, so the list updates without a reload.
export const updatePayFileInSearchStore = (
  payFileId: string,
  changes: Pick<PayFileListFieldsFragment, "status" | "latestLog">
): void =>
  usePayFileSearchStore.setState((state) => ({
    data: state.data.map((payFile) => (payFile.payFileId === payFileId ? { ...payFile, ...changes } : payFile)),
  }))
